import { useState } from 'react'

import { useFloorPlan } from '../../hooks/useFloorPlan'
import { useTourPlanCheckpoints } from '../hooks/useTourPlanCheckpoints'
import { useLightMode } from '@/hooks/useLightMode'

import { CheckpointIProps } from '@/features/floor-plan'
import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { selectEditMode } from '@/redux/ui/uiSlice'
import { trackEvent } from '@/utils/analytics'

import { DarkEditCheckPoint } from '../../checkpoints-panel/dark/DarkEditCheckPoint'
import { DarkEditTourPlan } from './DarkEditTourPlan'
import { DarkTourPlanCheckPointsList } from './DarkTourPlanCheckPointsList'

enum TourPlanState {
  CheckpointsList,
  EditCheckpoint,
  EditTourPlan,
}

export interface DarkTourPlanCheckPointsIProps {
  facilityId: string
  tourPlanId: string
  currentFloorId: string
  isExpanded: boolean
  onToggleExpansion: () => void
  onClose: () => void
  onUpdateTourPlan: (id?: string) => void
  onDeleteTourPlan: () => void
  onDeviceItemHoverIn: (deviceId: string) => void
}

export const DarkTourPlanCheckPoints = ({
  facilityId,
  tourPlanId,
  currentFloorId,
  isExpanded,
  onToggleExpansion,
  onClose,
  onUpdateTourPlan,
  onDeleteTourPlan,
  onDeviceItemHoverIn,
}: DarkTourPlanCheckPointsIProps) => {
  const colorScheme = useLightMode()
  const me = useAppSelector(selectMe)
  const isEditMode = useAppSelector(selectEditMode)
  const { id, name, checkpoints, isLoading } =
    useTourPlanCheckpoints(tourPlanId)
  const [editCheckPoint, setEditCheckPoint] = useState<CheckpointIProps>(null)
  const [viewState, setViewState] = useState<TourPlanState>(
    TourPlanState.CheckpointsList
  )
  const { setHighlightedDeviceIds, setTourStopDeviceIds } = useFloorPlan()

  const handleOnClick = (checkpoint: CheckpointIProps) => {
    setEditCheckPoint(checkpoint)
    setViewState(TourPlanState.EditCheckpoint)
    trackEvent('Button Press: Tour Plan Details - Checkpoint', {
      facility_id: facilityId,
      tour_plan_id: tourPlanId,
      checkpoint_id: checkpoint?.id,
      role: me.roleName,
    })
    isEditMode &&
      trackEvent('Button Press: Tour Plan Details - Checkpoint Edit', {
        facility_id: facilityId,
        tour_plan_id: tourPlanId,
        checkpoint_id: checkpoint?.id,
        role: me.roleName,
      })
  }

  const handleEditClick = () => {
    setViewState(TourPlanState.EditTourPlan)
    trackEvent('Button Press: Tour Plan Details - Edit Tour', {
      facility_id: facilityId,
      tour_plan_id: tourPlanId,
      role: me.roleName,
    })
  }

  const handleOnClose = () => {
    isEditMode &&
      trackEvent('Button Press: Tour Plan Details - Checkpoint Edit Cancel', {
        facility_id: facilityId,
        tour_plan_id: tourPlanId,
        checkpoint_id: editCheckPoint?.id,
        role: me.roleName,
      })
    setEditCheckPoint(null)
    setHighlightedDeviceIds(null)
    setTourStopDeviceIds(null)
    setViewState(TourPlanState.CheckpointsList)
  }

  const handleOnDone = () => {
    setHighlightedDeviceIds(null)
    setTourStopDeviceIds(null)
    onClose()
    trackEvent('Button Press: Tour Plan Details - Checkpoint Edit Save', {
      facility_id: editCheckPoint?.facility?.id,
      tour_plan_id: tourPlanId,
      checkpoint_id: editCheckPoint?.id,
      role: me.roleName,
    })
  }

  const backToTourCheckpoints = (id?: string) => {
    setViewState(TourPlanState.CheckpointsList)
    if (id) {
      onUpdateTourPlan(id)
    }
  }

  if (viewState === TourPlanState.EditCheckpoint) {
    // The dark editor owns its own rail container, so it replaces the wrapper
    // rather than sitting inside one.
    return (
      <DarkEditCheckPoint
        checkpoint={editCheckPoint}
        colorScheme={colorScheme}
        isExpanded={isExpanded}
        onClose={handleOnClose}
        onDone={handleOnDone}
        onToggleExpansion={onToggleExpansion}
      />
    )
  }

  if (viewState === TourPlanState.EditTourPlan) {
    return (
      <DarkEditTourPlan
        checkpoints={checkpoints}
        currentFloorId={currentFloorId}
        facilityId={facilityId}
        id={id}
        isExpanded={isExpanded}
        isLoading={isLoading}
        name={name}
        onClose={backToTourCheckpoints}
        onDeleteTourPlan={onDeleteTourPlan}
        onToggleExpansion={onToggleExpansion}
        onUpdateTourPlan={backToTourCheckpoints}
      />
    )
  }

  return (
    <DarkTourPlanCheckPointsList
      checkpoints={checkpoints}
      isExpanded={isExpanded}
      isLoading={isLoading}
      name={name}
      onCheckPointClick={handleOnClick}
      onClose={onClose}
      onDeviceItemHoverIn={onDeviceItemHoverIn}
      onEditClick={handleEditClick}
      onToggleExpansion={onToggleExpansion}
    />
  )
}
